import { UploadZone } from "@components:UploadZone";
import { useToasterStoreActions } from "@stores:global";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const ALLOWED_SVG_TYPES = ["image/svg+xml"];

/**
 * Sanitizes SVG content by removing potentially dangerous elements and attributes.
 * Strips script tags, event handlers, and external references to prevent XSS.
 */
const sanitizeSvg = (svgContent: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, "image/svg+xml");

  // Check for parsing errors
  const parserError = doc.querySelector("parsererror");
  if (parserError) {
    throw new Error("Invalid SVG format");
  }

  // Remove dangerous elements
  const dangerousElements = [
    "script",
    "iframe",
    "object",
    "embed",
    "foreignObject",
  ];
  dangerousElements.forEach((tag) => {
    doc.querySelectorAll(tag).forEach((el) => el.remove());
  });

  // Remove event handler attributes and javascript: URLs
  const allElements = doc.querySelectorAll("*");
  allElements.forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = attr.value.toLowerCase();
      // Remove event handlers (on*)
      if (name.startsWith("on")) {
        el.removeAttribute(attr.name);
      }
      // Remove javascript: and data: URLs (except data:image for embedded images)
      if (
        value.includes("javascript:") ||
        (value.includes("data:") && !value.includes("data:image"))
      ) {
        el.removeAttribute(attr.name);
      }
      // Remove xlink:href with javascript
      if (name === "xlink:href" && value.includes("javascript:")) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return new XMLSerializer().serializeToString(doc);
};

interface SvgUploadZoneProps {
  value: string | null | undefined;
  onChange: (base64: string) => void;
  onRemove: () => void;
}

export const SvgUploadZone = ({ value, onChange }: SvgUploadZoneProps) => {
  const { t } = useTranslation();
  const toastActions = useToasterStoreActions();
  const readerRef = useRef<FileReader | null>(null);

  // Cleanup FileReader on unmount
  useEffect(() => {
    return () => {
      if (readerRef.current?.readyState === 1) {
        readerRef.current.abort();
      }
    };
  }, []);

  const handleFileSelect = useCallback(
    (file: File) => {
      // Validate file type
      if (!ALLOWED_SVG_TYPES.includes(file.type)) {
        toastActions.setToaster({
          open: true,
          message: t("preferences.uiBranding.svgOnly"),
          variant: "error",
        });
        return;
      }

      if (file.size > 50 * 1024) {
        toastActions.setToaster({
          open: true,
          message: t("preferences.uiBranding.svgTooLarge"),
          variant: "error",
        });
        return;
      }

      const reader = new FileReader();
      readerRef.current = reader;
      reader.onload = () => {
        try {
          const svgText = reader.result as string;
          // Sanitize SVG content to prevent XSS
          const sanitized = sanitizeSvg(svgText);
          // Convert sanitized SVG back to base64
          const base64 = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(sanitized)))}`;
          onChange(base64);
        } catch {
          toastActions.setToaster({
            open: true,
            message: t("preferences.uiBranding.svgInvalid"),
            variant: "error",
          });
        }
      };
      reader.onerror = () => {
        toastActions.setToaster({
          open: true,
          message: t("preferences.uiBranding.fileReadError"),
          variant: "error",
        });
      };
      reader.readAsText(file);
    },
    [onChange, toastActions, t],
  );

  return (
    <UploadZone
      size="compact"
      onFileSelect={handleFileSelect}
      selectedFile={null}
      previewUrl={value}
      accept="image/svg+xml"
      title={t("preferences.uiBranding.svgDropTitle")}
      hint={t("preferences.uiBranding.svgHint")}
    />
  );
};
